import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "../../utils/cn";
import { normalizeArticleMarkdownContent } from "../../utils/helpCenterMarkdown";

interface MarkdownContentProps {
  content: string;
  className?: string;
}

export function MarkdownContent({ content, className }: MarkdownContentProps) {
  const markdown = normalizeArticleMarkdownContent(content);

  if (!markdown.trim()) {
    return (
      <p className="text-sm text-slate-500 italic">No article content yet.</p>
    );
  }

  return (
    <div
      className={cn(
        "markdown-content text-slate-600 leading-relaxed text-sm",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-3 first:mt-0">
              {children}
            </h2>
          ),
          h2: ({ children }) => (
            <h3 className="text-lg font-bold text-slate-900 mt-6 mb-2">
              {children}
            </h3>
          ),
          h3: ({ children }) => (
            <h4 className="text-base font-semibold text-slate-900 mt-5 mb-2">
              {children}
            </h4>
          ),
          p: ({ children }) => (
            <p className="mb-4 last:mb-0 text-slate-600">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-1.5 mb-4 text-slate-600">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside space-y-1.5 mb-4 text-slate-600">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="pl-1">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary-200 pl-4 py-1 my-4 text-slate-600 bg-slate-50/80 rounded-r-lg">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              className="text-primary-600 hover:text-primary-700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4 rounded-lg border border-slate-200">
              <table className="min-w-full text-left text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-slate-50 text-slate-700 font-semibold">
              {children}
            </thead>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2 border-b border-slate-200">{children}</th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2 border-b border-slate-100">{children}</td>
          ),
          code: ({ className: codeClass, children }) => {
            const isBlock = codeClass?.includes("language-");
            if (isBlock) {
              return (
                <pre className="mb-4 p-3 rounded-lg bg-slate-900 text-slate-100 text-xs overflow-x-auto">
                  <code>{children}</code>
                </pre>
              );
            }
            return (
              <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-xs font-mono">
                {children}
              </code>
            );
          },
          hr: () => <hr className="my-6 border-slate-200" />,
          strong: ({ children }) => (
            <strong className="font-semibold text-slate-800">{children}</strong>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownContent;
